import { useEffect, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type { CohortInsightsResponse } from '../../api/types';

export interface VinCohortInsights {
  data: CohortInsightsResponse | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches cohort insights for a VIN. Lives outside the card component so a
 * consumer that renders the sections in more than one place — the Client
 * pop-out splits price from placement — can share a single request.
 */
export function useVinCohortInsights(vin: string, enabled = true): VinCohortInsights {
  const [data, setData] = useState<CohortInsightsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vin || !enabled) return;
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    siftApiClient
      .getVehicleCohortInsights(vin, controller.signal)
      .then(setData)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError('Could not load cohort insights.');
        console.error('useVinCohortInsights load failed', err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [vin, enabled]);

  return { data, isLoading, error };
}
